import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import type { AuthChangeEvent } from '@supabase/supabase-js';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string, options: { redirectTo: string, captchaToken?: string }) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider - Initializing...');
    // Initialize session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log('AuthProvider - Initial session:', {
        hasSession: !!initialSession,
        userId: initialSession?.user?.id,
        userEmail: initialSession?.user?.email
      });
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log('AuthProvider - Auth state change:', {
        event,
        hasSession: !!currentSession,
        userId: currentSession?.user?.id,
        userEmail: currentSession?.user?.email
      });
      
      if (event === 'SIGNED_IN') {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
      } else if (event === 'TOKEN_REFRESHED') {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      } else if (event === 'USER_UPDATED') {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      }
      
      setLoading(false);
    });

    return () => {
      console.log('AuthProvider - Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, []);

  const handleSignUp = async (email: string, password: string) => {
    const { data: { session: newSession }, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) throw error;
    
    setSession(newSession);
    setUser(newSession?.user ?? null);
  };

  const handleSignIn = async (email: string, password: string) => {
    const { data: { session: newSession }, error } = await supabase.auth.signInWithPassword({ 
      email, 
      password 
    });
    
    if (error) {
      if (error.message === 'Invalid login credentials') {
        throw new Error('Email ou senha inválidos');
      }
      throw error;
    }
    
    setSession(newSession);
    setUser(newSession?.user ?? null);
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    setSession(null);
    setUser(null);
  };

  const handleResetPasswordForEmail = async (email: string, options: { redirectTo: string, captchaToken?: string }) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, options);
    if (error) throw error;
  };

  const handleUpdatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  const value = {
    session,
    user,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    resetPasswordForEmail: handleResetPasswordForEmail,
    updatePassword: handleUpdatePassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
